/** Small presentational building blocks shared by both apps. */
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Icon, type IconName } from "./Icon";
import type { Tone } from "../lib/vocab";

/* ---------------------------------------------------------------------- *
 * Button
 * ---------------------------------------------------------------------- */
type Variant = "primary" | "subtle" | "ghost" | "outline" | "danger" | "danger-ghost";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: "sm" | "md" | "lg";
  icon?: IconName;
  iconOnly?: boolean;
  block?: boolean;
  active?: boolean;
  loading?: boolean;
}

export function Button({
  variant = "subtle",
  size = "md",
  icon,
  iconOnly,
  block,
  active,
  loading,
  className = "",
  children,
  disabled,
  ...rest
}: ButtonProps) {
  const classes = [
    "btn",
    `btn--${variant}`,
    size !== "md" && `btn--${size}`,
    iconOnly && "btn--icon",
    block && "btn--block",
    active && "btn--on",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <button type="button" className={classes} disabled={disabled || loading} {...rest}>
      {loading ? <span className="spinner" /> : icon ? <Icon name={icon} size={size === "sm" ? 13 : 15} /> : null}
      {children}
    </button>
  );
}

/* ---------------------------------------------------------------------- *
 * Badge
 * ---------------------------------------------------------------------- */
interface BadgeProps {
  tone?: Tone;
  title?: string;
  dot?: boolean;
  outline?: boolean;
  onClick?: () => void;
  children: ReactNode;
}

export function Badge({ tone = "neutral", title, dot, outline, onClick, children }: BadgeProps) {
  const cls = [
    "badge",
    tone !== "neutral" && `badge--${tone}`,
    outline && "badge--outline",
  ]
    .filter(Boolean)
    .join(" ");
  const inner = (
    <>
      {dot && <span className="badge__dot" />}
      {children}
    </>
  );
  if (onClick) {
    return (
      <button type="button" className={cls} title={title} onClick={onClick}>
        {inner}
      </button>
    );
  }
  return (
    <span className={cls} title={title}>
      {inner}
    </span>
  );
}

/** A status badge driven by the shared vocabulary. */
export function StatusBadge({
  value,
  dict,
  fallbackLabel,
}: {
  value: string | null | undefined;
  dict: Record<string, { label: string; tone: Tone; help: string }>;
  fallbackLabel?: string;
}) {
  if (!value) return <span className="muted">{fallbackLabel ?? "—"}</span>;
  const meta = dict[value];
  if (!meta) return <Badge title={value}>{value}</Badge>;
  return (
    <Badge tone={meta.tone} title={`${meta.help}  (${value})`}>
      {meta.label}
    </Badge>
  );
}

/* ---------------------------------------------------------------------- *
 * Card
 * ---------------------------------------------------------------------- */
export function Card({
  title,
  subtitle,
  actions,
  flush,
  danger,
  className = "",
  children,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  flush?: boolean;
  danger?: boolean;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <section className={`card ${danger ? "card--danger" : ""} ${className}`}>
      {(title || actions) && (
        <header className="card__hd">
          {title && <h2>{title}</h2>}
          {subtitle && <span className="sub">{subtitle}</span>}
          {actions && (
            <>
              <span className="grow" />
              {actions}
            </>
          )}
        </header>
      )}
      <div className={`card__bd ${flush ? "card__bd--flush" : ""}`}>{children}</div>
    </section>
  );
}

/* ---------------------------------------------------------------------- *
 * States
 * ---------------------------------------------------------------------- */
export function EmptyState({
  icon = "inbox",
  title,
  hint,
  action,
  compact,
}: {
  icon?: IconName;
  title: string;
  hint?: string;
  action?: ReactNode;
  compact?: boolean;
}) {
  return (
    <div className={`empty ${compact ? "empty--compact" : ""}`}>
      <div className="empty__icon">
        <Icon name={icon} size={20} />
      </div>
      <h3>{title}</h3>
      {hint && <p>{hint}</p>}
      {action}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <EmptyState
      icon="alert"
      title="Could not load"
      hint={message}
      action={
        onRetry ? (
          <Button icon="refresh" onClick={onRetry}>
            Try again
          </Button>
        ) : undefined
      }
    />
  );
}

export function SkeletonTable({ cols, rows = 6 }: { cols: number; rows?: number }) {
  return (
    <div className="tablewrap">
      <table className="table">
        <tbody>
          {Array.from({ length: rows }, (_, r) => (
            <tr key={r}>
              {Array.from({ length: cols }, (_, c) => (
                <td key={c}>
                  <div className="skeleton" style={{ width: `${c === 0 ? 70 : 34 + ((c * 13) % 40)}%` }} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------------------------------------------------------------- *
 * Misc
 * ---------------------------------------------------------------------- */
export function SectionTitle({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="section-title">
      {children}
      {aside}
    </div>
  );
}

/** A thumbnail plus two lines — the shape used for every video row. */
export function Media({
  thumb,
  title,
  sub,
  href,
}: {
  thumb?: string | null;
  title?: string | null;
  sub?: string | null;
  href?: string | null;
}) {
  return (
    <div className="media">
      {thumb ? (
        <img className="media__thumb" src={thumb} loading="lazy" alt="" />
      ) : (
        <div className="media__ph">
          <Icon name="play" size={13} />
        </div>
      )}
      <div className="media__txt">
        <div className="media__t1 truncate" title={title ?? undefined}>
          {title || "(untitled)"}
        </div>
        <div className="media__t2 truncate" title={sub ?? undefined}>
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              {sub || href}
            </a>
          ) : (
            sub || ""
          )}
        </div>
      </div>
    </div>
  );
}

export function TimeCell({ value, absolute }: { value: string | null | undefined; absolute: string }) {
  if (!value) return <span className="muted">—</span>;
  return (
    <span className="nowrap muted" title={absolute}>
      {value}
    </span>
  );
}
